/**
 * Derive what the booking flow currently knows about a booking's price.
 *
 * Extracted from `PaymentDetailsStep` deliberately: that component is ~1300 lines
 * behind a dozen providers, and this decision governs whether a member gets charged.
 * As a pure function it can be tested directly.
 *
 * @module pages/booking/steps/bookingQuote
 */

import type { BookingQuote } from '@butter/library/pricing';

export interface BookingQuoteInputs {
  /**
   * The subscription query has settled (success or error), not merely "not
   * loading" — the member path derives `finalTotal` from that payload.
   *
   * A guest runs no subscription query and is settled by definition: pass `true`.
   */
  subscriptionSettled: boolean;
  /** Whether the validation query is enabled at all — it is gated on several params. */
  validationEnabled: boolean;
  /** Validation request in flight. */
  isValidating: boolean;
  /** Validation request errored. */
  validationFailed: boolean;
  /** True once validation has returned a usable payload. */
  hasValidationData: boolean;
  /** `subscriptionPricing.finalTotal` — only meaningful once validation has landed. */
  finalTotal: number;
  /**
   * The server refused this booking outright (subscription-only club, bay gate,
   * or a pricing failure) rather than returning a price. Only meaningful for a
   * non-member — computed at the call site as
   * `validationData?.isValid === false && !validationData?.canProceedWithPayment`.
   */
  validationRefused: boolean;
  /** The quote's `paymentTiming` — `'at_club'` when the club collects at the counter. */
  paymentTiming?: 'now' | 'at_club';
  /**
   * The signed-in quote's card verdict (credits D29) — `false` when the member's account
   * credit covers their share. Absent on a guest quote.
   */
  paymentMethodRequired?: boolean;
}

/**
 * Map the flow's flags onto a `BookingQuote`.
 *
 * Everything that is not "we asked the server and it answered" is `pending`. That
 * matters because the caller turns `pending` into `'unknown'`, which blocks submit —
 * and each of these states previously produced a number the UI treated as real:
 *
 * 1. **Validating.** The pricing memo returns the client-computed *walk-in* total
 *    while in flight. Non-zero in the normal case, so it happened to fail closed —
 *    but it can be `0` when the time/date/timezone are missing or the price
 *    calculation threw, and then it failed open.
 * 2. **Query disabled or idle.** React Query reports `isLoading: false` with no data,
 *    so the memo fell through to a branch commented "assume covered (safe default)"
 *    and returned `finalTotal: 0`. Nothing was safe about it: the server still
 *    charged, on the card the flow had already auto-selected.
 * 3. **Query failed.** Indistinguishable from #2 today — a failed price check also
 *    read as covered.
 * 4. **Subscription not settled.** `hasActiveSubscription` is `false` while loading,
 *    so a member briefly took the walk-in branch and was shown the walk-in total.
 * 5. **Non-member, server refused.** `isValid: false` with no chargeable fees — a
 *    subscription-only club, a bay the booker isn't allowed, or a pricing failure.
 *    Mapped to `pending` rather than a distinct status: `BookingQuote` (from
 *    `@butter/library/pricing`) has no `refused` member, so a new status would be
 *    a compile error in `resolvePaymentRequirement`'s exhaustiveness check.
 *    `pending` already blocks submit; the refusal message itself is rendered by
 *    the caller straight from `errors`, not carried on the quote.
 * 6. **Guest checkout.** This used to return `walk-in` outright, on the reasoning
 *    that there was nothing to look up. There is now: `POST /bookings/quote`
 *    prices a booker with no account, so a guest waits for the server exactly as
 *    a member does. **This function no longer produces `'walk-in'` at all** — the
 *    client has no "we'll work it out ourselves" state left. Either the server
 *    priced it, or we don't know.
 *
 * What is left is one rule, applied to everyone: the server priced it, or we do
 * not know. The old branching on auth state and plan state decided nothing once
 * every booker had a server quote, so it went with the fallback it guarded.
 */
export function deriveBookingQuote(inputs: BookingQuoteInputs): BookingQuote {
  const {
    subscriptionSettled,
    validationEnabled,
    isValidating,
    validationFailed,
    hasValidationData,
    finalTotal,
    validationRefused,
    paymentTiming,
    paymentMethodRequired,
  } = inputs;

  // The member path derives `finalTotal` from the subscription payload, so an
  // unsettled subscription means the number is not trustworthy yet.
  if (!subscriptionSettled) return { status: 'pending' };

  // One rule for every booker — guest, non-member and member alike. Anything short
  // of a returned payload is unknown, never free and never locally computed.
  if (!validationEnabled || isValidating || validationFailed || !hasValidationData) {
    return { status: 'pending' };
  }

  // Refused rather than priced. The gates run for every plan type, so this is a
  // guest at a subscription-only club and a member on a bay their plan cannot open.
  if (validationRefused) return { status: 'pending' };

  return {
    status: 'quoted',
    total: finalTotal,
    ...(paymentTiming && { paymentTiming }),
    ...(paymentMethodRequired !== undefined && { paymentMethodRequired }),
  };
}
